/**
 * Patched version of @radix-ui/react-slot for React 19 compatibility.
 *
 * The original Slot uses `composeRefs()` (plain function) during render,
 * creating a NEW callback ref identity every render. React 19 detaches
 * old refs and re-attaches new refs when identity changes, causing:
 *   render → new composeRefs() → React detach(null)/attach(el) → setNode(el) → re-render → loop
 *
 * Fix: Replace `composeRefs()` in SlotClone with `useComposedRefs()` (hook),
 * which returns a stable callback via useCallback — identity never changes,
 * so React 19 never detaches/re-attaches.
 *
 * See: https://github.com/radix-ui/primitives/issues/3799
 */
import * as React from 'react';
import { useComposedRefs } from './radix-compose-refs';
import { jsx, Fragment } from 'react/jsx-runtime';

const REACT_LAZY_TYPE = Symbol.for('react.lazy');
// Safely access React.use (React 19's hook for consuming promises in render)
const use = (React as any).use;

function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return typeof value === 'object' && value !== null && 'then' in value;
}

function isLazyComponent(element: unknown): boolean {
  return (
    element != null &&
    typeof element === 'object' &&
    '$$typeof' in (element as any) &&
    (element as any).$$typeof === REACT_LAZY_TYPE &&
    '_payload' in (element as any) &&
    isPromiseLike((element as any)._payload)
  );
}

type PossibleRef<T> = React.Ref<T> | undefined;

/* ------------------------------------------------------------------ */
/* SlotClone — the key fix: useComposedRefs instead of composeRefs    */
/* ------------------------------------------------------------------ */
function createSlotClone(_ownerName: string) {
  const SlotClone = React.forwardRef<any, any>((props, forwardedRef) => {
    let { children, ...slotProps } = props;

    if (isLazyComponent(children) && typeof use === 'function') {
      children = use((children as any)._payload);
    }

    // Extract child ref BEFORE hooks so we can always call useComposedRefs
    const childrenRef: PossibleRef<any> = React.isValidElement(children)
      ? getElementRef(children)
      : null;

    // PATCHED: useComposedRefs (hook) returns a STABLE callback ref identity.
    // The original used composeRefs() (plain function) which created a new
    // function every render, triggering React 19's infinite detach/attach loop.
    const composedRef = useComposedRefs(forwardedRef, childrenRef);

    if (React.isValidElement(children)) {
      const props2 = mergeProps(slotProps, (children as React.ReactElement<any>).props);
      if ((children as React.ReactElement<any>).type !== React.Fragment) {
        props2.ref = forwardedRef ? composedRef : childrenRef;
      }
      return React.cloneElement(children as React.ReactElement<any>, props2);
    }

    return React.Children.count(children) > 1 ? React.Children.only(null) : null;
  });
  SlotClone.displayName = `${_ownerName}.SlotClone`;
  return SlotClone;
}

/* ------------------------------------------------------------------ */
/* Slot                                                                */
/* ------------------------------------------------------------------ */
function createSlot(ownerName: string) {
  const SlotClone = createSlotClone(ownerName);
  const Slot2 = React.forwardRef<any, any>((props, forwardedRef) => {
    let { children, ...slotProps } = props;

    if (isLazyComponent(children) && typeof use === 'function') {
      children = use((children as any)._payload);
    }

    const childrenArray = React.Children.toArray(children);
    const slottable = childrenArray.find(isSlottable);

    if (slottable) {
      const newElement = (slottable as React.ReactElement<any>).props.children;
      const newChildren = childrenArray.map((child) => {
        if (child === slottable) {
          if (React.Children.count(newElement) > 1) return React.Children.only(null);
          return React.isValidElement(newElement)
            ? (newElement as React.ReactElement<any>).props.children
            : null;
        } else {
          return child;
        }
      });
      return jsx(SlotClone, {
        ...slotProps,
        ref: forwardedRef,
        children: React.isValidElement(newElement)
          ? React.cloneElement(newElement as React.ReactElement<any>, undefined, newChildren)
          : null,
      });
    }

    return jsx(SlotClone, { ...slotProps, ref: forwardedRef, children });
  });
  Slot2.displayName = `${ownerName}.Slot`;
  return Slot2;
}

const Slot = createSlot('Slot');

/* ------------------------------------------------------------------ */
/* Slottable                                                           */
/* ------------------------------------------------------------------ */
const SLOTTABLE_IDENTIFIER = Symbol('radix.slottable');

function createSlottable(ownerName: string) {
  const Slottable2 = ({ children }: { children: React.ReactNode }) => {
    return jsx(Fragment, { children });
  };
  (Slottable2 as any).displayName = `${ownerName}.Slottable`;
  (Slottable2 as any).__radixId = SLOTTABLE_IDENTIFIER;
  return Slottable2;
}

const Slottable = createSlottable('Slottable');

function isSlottable(child: React.ReactNode): boolean {
  return (
    React.isValidElement(child) &&
    typeof child.type === 'function' &&
    '__radixId' in child.type &&
    (child.type as any).__radixId === SLOTTABLE_IDENTIFIER
  );
}

/* ------------------------------------------------------------------ */
/* mergeProps / getElementRef — unchanged from original                */
/* ------------------------------------------------------------------ */
function mergeProps(slotProps: Record<string, any>, childProps: Record<string, any>) {
  const overrideProps: Record<string, any> = { ...childProps };
  for (const propName in childProps) {
    const slotPropValue = slotProps[propName];
    const childPropValue = childProps[propName];
    const isHandler = /^on[A-Z]/.test(propName);
    if (isHandler) {
      if (slotPropValue && childPropValue) {
        overrideProps[propName] = (...args: unknown[]) => {
          const result = childPropValue(...args);
          slotPropValue(...args);
          return result;
        };
      } else if (slotPropValue) {
        overrideProps[propName] = slotPropValue;
      }
    } else if (propName === 'style') {
      overrideProps[propName] = { ...slotPropValue, ...childPropValue };
    } else if (propName === 'className') {
      overrideProps[propName] = [slotPropValue, childPropValue].filter(Boolean).join(' ');
    }
  }
  return { ...slotProps, ...overrideProps };
}

function getElementRef(element: React.ReactElement<any>): PossibleRef<any> {
  let getter = Object.getOwnPropertyDescriptor(element.props, 'ref')?.get;
  let mayWarn = getter && 'isReactWarning' in getter && (getter as any).isReactWarning;
  if (mayWarn) {
    return (element as any).ref;
  }
  getter = Object.getOwnPropertyDescriptor(element, 'ref')?.get;
  mayWarn = getter && 'isReactWarning' in getter && (getter as any).isReactWarning;
  if (mayWarn) {
    return element.props.ref;
  }
  return element.props.ref || (element as any).ref;
}

export { Slot, Slot as Root, Slottable, createSlot, createSlottable };
